import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, Clock, CheckCircle, Syringe, Pill, HelpCircle, ChevronRight, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d) => { if (!d) return 'N/D'; const p = d.slice(0, 10).split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };

function RowItem({ icon: Icon, iconClass, bg, title, subtitle, animalId, testid, onIgnore, onClickAnimal }) {
  return (
    <div className={`w-full flex items-center gap-2 ${bg} rounded-xl p-3 hover:brightness-95 transition-all`}>
      <button
        type="button"
        data-testid={testid}
        onClick={() => onClickAnimal(animalId)}
        className="flex items-center gap-3 flex-1 min-w-0 text-left">
        <Icon className={`w-4 h-4 flex-shrink-0 ${iconClass}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{title}</p>
          <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </button>
      {onIgnore && (
        <button
          type="button"
          onClick={onIgnore}
          data-testid={`${testid}-ignorar`}
          title="Ignorar este alerta permanentemente"
          className="flex-shrink-0 p-1.5 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export default function CalendarPage() {
  const [alertas, setAlertas] = useState([]);
  const [tratamentos, setTratamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const carregar = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/calendario/alertas`),
      axios.get(`${API}/tratamentos/semanais`)
    ]).then(([a, t]) => {
      setAlertas(a.data);
      setTratamentos(t.data);
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { carregar(); }, []);

  if (loading) return <div className="text-center py-12 text-muted-foreground">A carregar...</div>;

  const atrasadas = alertas.filter(a => a.tipo === 'vacina_atrasada');
  const proximas = alertas.filter(a => a.tipo === 'vacina_proxima');
  const semProxima = alertas.filter(a => a.tipo === 'vacina_sem_proxima');

  const goToAnimal = (animalId) => {
    if (animalId) navigate(`/?animal=${animalId}`);
  };

  const ignorarVacina = async (e, vacinaId) => {
    e.stopPropagation();
    if (!vacinaId) { alert('Este alerta não permite ignorar (registo antigo).'); return; }
    if (!window.confirm('Ignorar este alerta permanentemente? A vacina não voltará a aparecer nas atrasadas.')) return;
    try {
      await axios.post(`${API}/vacinas/${vacinaId}/ignorar`, { ignorar: true });
      carregar();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro a ignorar alerta');
    }
  };

  return (
    <div data-testid="calendar-page">
      <h2 className="font-heading font-bold text-2xl mb-6">Calendário e Alertas</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-card rounded-2xl border border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            <h3 className="font-heading font-bold text-lg">Vacinas Atrasadas ({atrasadas.length})</h3>
          </div>
          {atrasadas.length === 0 ? (
            <div className="flex items-center gap-2 text-green-600 text-sm"><CheckCircle className="w-4 h-4" />Todas as vacinas em dia!</div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {atrasadas.map((a, i) => (
                <RowItem key={`atr-${a.animal_id}-${a.vacina}-${a.data}-${i}`}
                  icon={Syringe}
                  iconClass="text-destructive"
                  bg="bg-destructive/5"
                  title={`${a.animal?.nome || 'Animal'} - ${a.vacina}`}
                  subtitle={`Atrasada desde ${fmtDate(a.data)}${a.animal?.box ? ` | Box: ${a.animal.box}` : ''}`}
                  animalId={a.animal_id}
                  testid={`atrasada-${i}`}
                  onIgnore={(e) => ignorarVacina(e, a.vacina_id)}
                  onClickAnimal={goToAnimal}
                />
              ))}
            </div>
          )}
        </div>

        <div className="bg-card rounded-2xl border border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-accent" />
            <h3 className="font-heading font-bold text-lg">Vacinas Próximas ({proximas.length})</h3>
          </div>
          {proximas.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma vacina agendada para os próximos 7 dias.</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {proximas.map((a, i) => (
                <RowItem key={`prox-${a.animal_id}-${a.vacina}-${a.data}-${i}`}
                  icon={Syringe}
                  iconClass="text-accent"
                  bg="bg-accent/10"
                  title={`${a.animal?.nome || 'Animal'} - ${a.vacina}`}
                  subtitle={`Agendada: ${fmtDate(a.data)}${a.animal?.box ? ` | Box: ${a.animal.box}` : ''}`}
                  animalId={a.animal_id}
                  testid={`proxima-${i}`}
                  onClickAnimal={goToAnimal}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Vacinas sem data da próxima — apenas informativo */}
      <div className="bg-card rounded-2xl border border-border p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <HelpCircle className="w-5 h-5 text-amber-600" />
          <h3 className="font-heading font-bold text-lg">Vacinas sem Data da Próxima ({semProxima.length})</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Estas vacinas não foram marcadas como atrasadas porque não têm data de próxima administração definida. Clica para ir à ficha do animal e atualizar.</p>
        {semProxima.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma vacina pendente de agendamento.</p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {semProxima.map((a, i) => (
              <RowItem key={`semp-${a.animal_id}-${a.vacina}-${a.data || i}`}
                icon={Syringe}
                iconClass="text-amber-600"
                bg="bg-amber-50"
                title={`${a.animal?.nome || 'Animal'} - ${a.vacina}`}
                subtitle={`Administrada em: ${fmtDate(a.data)}${a.animal?.box ? ` | Box: ${a.animal.box}` : ''}`}
                animalId={a.animal_id}
                testid={`sem-proxima-${i}`}
                onClickAnimal={goToAnimal}
              />
            ))}
          </div>
        )}
      </div>

      <div className="bg-card rounded-2xl border border-border p-6">
        <div className="flex items-center gap-2 mb-4">
          <Pill className="w-5 h-5 text-primary" />
          <h3 className="font-heading font-bold text-lg">Tratamentos Ativos ({tratamentos.length})</h3>
        </div>
        {tratamentos.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum tratamento ativo no momento.</p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {tratamentos.map(t => (
              <RowItem key={t.id}
                icon={Pill}
                iconClass="text-primary"
                bg="bg-muted/30"
                title={`${t.animal?.nome || 'Animal'} - ${t.nome}`}
                subtitle={`Início: ${fmtDate(t.data_inicio)}${t.animal?.box ? ` | Box: ${t.animal.box}` : ''}${t.descricao ? ` | ${t.descricao}` : ''}`}
                animalId={t.animal_id || t.animal?.id}
                testid={`tratamento-${t.id}`}
                onClickAnimal={goToAnimal}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
